import { inject, Injectable } from '@angular/core';
import { GetFollowUpsListAction } from '@app/modules/follow-ups/store/follow-ups.actions';
import { FollowUpsState } from '@app/modules/follow-ups/store/follow-ups.state';
import { ResultDetailsDialogComponent } from '@app/modules/reports/components/results/result-details-dialog/result-details-dialog.component';
import { ReportsUtilsService } from '@app/modules/reports/services/reports-utils.service';
import { GetReportsListAction } from '@app/modules/reports/store/reports.actions';
import { ReportsState } from '@app/modules/reports/store/reports.state';
import {
  ArchiveResultAction,
  GetResultAction,
} from '@app/modules/reports/store/results.actions';
import {
  ResultsFilter,
  ResultsState,
} from '@app/modules/reports/store/results.state';
import {
  ExtendedResult,
  Result,
  ResultExtensions,
} from '@app/modules/reports/types/result.types';
import { SigneeState } from '@core/store/signees/signees.state';
import { HttpUtils } from '@core/utils/http-utils';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngxs/store';
import { ProgressDialogComponent } from '@shared/components/progress/progress-dialog.component';
import { ReasonComponent } from '@shared/components/reason/reason.component';
import { combineLatest, forkJoin, map, Observable } from 'rxjs';
import { of } from 'rxjs/internal/observable/of';
import { first, switchMap, tap } from 'rxjs/operators';
import { ResultSearchResultsComponent } from '../components/results/result-search-results/result-search-results.component';
import { ResultSearchComponent } from '../components/results/result-search/result-search.component';
@Injectable({
  providedIn: 'root',
})
export class ResultsUtilsService {
  private modal = inject(NgbModal);
  private store = inject(Store);
  private reportsUtils = inject(ReportsUtilsService);

  openResultDetails(resultId) {
    const modal = this.modal.open(ResultDetailsDialogComponent, {
      centered: true,
      backdrop: 'static',
      modalDialogClass: 'modal-size-lg',
    });

    modal.componentInstance.resultId.set(resultId);
  }

  openReportsOverview(resultId) {
    this.store
      .dispatch(new GetResultAction(resultId))
      .pipe(
        switchMap(() =>
          this.store
            .select(ResultsState.getResult)
            .pipe(map((getResultById) => getResultById(resultId)))
        ),
        first(),

        switchMap((result) => {
          if (!result?.reportIds?.length) {
            throw new Error('Result has no reports');
          }
          return this.store
            .dispatch(
              new GetReportsListAction(
                { ids: result.reportIds },
                {},
                null,
                null
              )
            )
            .pipe(
              switchMap(() => this.store.select(ReportsState.getReportsByIds)),
              map((getByIds) => getByIds(result.reportIds))
            );
        }),
        first(),
        switchMap((reports) => {
          const rootIds = reports.map((report) => report.rootId);
          return this.store
            .dispatch(new GetReportsListAction({ rootIds }, {}, null, null))
            .pipe(
              switchMap(() =>
                this.store.select(ReportsState.getReportsByRootIds)
              ),
              map((getByIds) => getByIds(rootIds))
            );
        }),
        first()
      )
      .subscribe((reports) =>
        this.reportsUtils.openReportsOverview(
          reports.map((report) => report.id)
        )
      );
  }

  public archiveResults(results: Result[]) {
    const modal = this.modal.open(ReasonComponent, {
      centered: true,
      backdrop: 'static',
      modalDialogClass: 'modal-size-md',
    });

    modal.closed
      .pipe(
        switchMap((reason) => {
          const progressDialogRef = this.modal.open(ProgressDialogComponent, {
            centered: true,
            backdrop: 'static',
            keyboard: false,
            modalDialogClass: 'modal-size-md',
          });
          progressDialogRef.componentInstance.title =
            'results.progress.archivingResults';

          return this.store
            .dispatch(
              results.map(
                (result) => new ArchiveResultAction(result.id, reason)
              )
            )
            .pipe(
              tap(() => {
                progressDialogRef.close();
              })
            );
        })
      )
      .subscribe();
  }

  openResultSearchDialog() {
    const modal = this.modal.open(ResultSearchComponent, {
      modalDialogClass: 'modal-size-md',
      centered: true,
    });
  }

  openResultSearchResultsDialog(resultIds: string[]) {
    const modal = this.modal.open(ResultSearchResultsComponent, {
      modalDialogClass: 'modal-size-xl',
      centered: true,
    });
    modal.componentInstance.resultIds.set(resultIds);
  }

  buildHttpParams(params: ResultsFilter) {
    let httpParams = HttpUtils.buildHttpParams(params);

    if (params.ids?.length) {
      httpParams = httpParams.set('ids', params.ids.join(','));
    }

    if (params.kitIds?.length) {
      httpParams = httpParams.set('kitIds', params.kitIds.join(','));
    }

    if (params.dataVersions?.length) {
      httpParams = httpParams.set(
        'dataVersions',
        params.dataVersions.join(',')
      );
    }

    if (params.laboratories?.length) {
      httpParams = httpParams.set(
        'laboratories',
        params.laboratories.join(',')
      );
    }

    if (params.lastStatuses?.length) {
      httpParams = httpParams.set(
        'lastStatuses',
        params.lastStatuses.join(',')
      );
    }

    return httpParams;
  }

  public getExtendedResults(
    results: Result[],
    appends: ResultExtensions = {
      followUps: true,
    }
  ): Observable<ExtendedResult[]> {
    const resultIds = results.map((result) => result.id);
    if (resultIds.length === 0) return of([]);
    return forkJoin([
      appends.followUps
        ? this.store.dispatch(
            new GetFollowUpsListAction(
              {
                ids: results.map((result) => result.followUpIds).flat(),
              },
              {},
              null,
              null
            )
          )
        : of(null),
    ]).pipe(
      switchMap(() =>
        combineLatest(
          results.map((result) =>
            combineLatest([
              of(result),
              appends.followUps
                ? this.store
                    .select(FollowUpsState.getFollowUpsByIds)
                    .pipe(
                      map((getFollowUpsByIds) =>
                        getFollowUpsByIds(result?.followUpIds)
                      )
                    )
                : of(null),
              this.store
                .select(SigneeState.getSigneeById)
                .pipe(map((getById) => getById(result?.signeeId))),
            ])
          )
        )
      ),
      map((items) =>
        items.map(([report, followUps, signee]) => ({
          ...report,
          followUps,
          signee,
        }))
      )
    );
  }
}
